import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import GalleryPage from "@/pages/GalleryPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsPage from "@/pages/TermsPage";
import DashboardSelector from "@/pages/DashboardSelector";
import DashboardSimulate from "@/pages/DashboardSimulate";
import SimulationDetailPage from "@/pages/SimulationDetailPage";
import AccountPage from "@/pages/AccountPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import SalesAllSimulationsPage from "@/pages/SalesAllSimulationsPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ResendVerificationPage from "@/pages/ResendVerificationPage";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import CookieBanner from "@/components/CookieBanner";
import useTrackPageView from "@/hooks/useTrackPageView";

import "@/App.css";

// Redirect already-logged-in users away from auth pages
function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    const dest = user.role === "admin" ? "/admin" : "/dashboard";
    return <Navigate to={dest} replace />;
  }
  return children;
}

// Gate the /dashboard SELECTOR for admins (they have their own landing page).
// Sub-routes /dashboard/interior and /dashboard/exterior are NOT gated — admins
// must be able to run simulations directly from their admin account without
// having to log out and back in as a sales user (feedback 22.05.2026).
function UserDashboardSelectorGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  return children;
}

// Stable role arrays referenced by <ProtectedRoute roles={…}>. Defined at
// module scope so we don't allocate a fresh array on every AppRoutes render —
// keeps the route-tree referentially stable.
const ROLES_ADMIN_ONLY = ["admin"];
const ROLES_SALES_OR_ADMIN = ["sales", "admin"];

// Toaster style is a stable object too (was inline → caused Toaster's memoised
// internals to recompute on every App render).
const TOASTER_STYLE = {
  background: "#0a0a0a",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "white",
};
const TOASTER_TOAST_OPTIONS = { style: TOASTER_STYLE };

function AppRoutes() {
  // iter 95 · Invoke tracking pixel on every route change
  useTrackPageView();
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/galerie" element={<GalleryPage />} />
      <Route path="/politica-confidentialitate" element={<PrivacyPolicyPage />} />
      <Route path="/termeni-si-conditii" element={<TermsPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/verify-email/resend" element={<ResendVerificationPage />} />
      <Route
        path="/login"
        element={
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        }
      />
      <Route
        path="/register"
        element={
          <PublicOnly>
            <RegisterPage />
          </PublicOnly>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <UserDashboardSelectorGuard>
              <DashboardSelector />
            </UserDashboardSelectorGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/interior"
        element={
          <ProtectedRoute>
            <DashboardSimulate simType="INTERIOR" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/exterior"
        element={
          <ProtectedRoute>
            <DashboardSimulate simType="EXTERIOR" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulation/:id"
        element={
          <ProtectedRoute>
            <SimulationDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/all"
        element={
          <ProtectedRoute roles={ROLES_SALES_OR_ADMIN}>
            <SalesAllSimulationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={ROLES_ADMIN_ONLY}>
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <ChangePasswordDialog />
          <CookieBanner />
        </AuthProvider>
        <Toaster
          theme="dark"
          richColors
          position="top-right"
          toastOptions={TOASTER_TOAST_OPTIONS}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
